import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, Edit2, Trash2, Upload, Download, 
  BarChart3, Users, CheckCircle, XCircle, Eye, Send, Clock, Calendar, ChevronDown, ChevronUp, Printer
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ModuleInfo } from './ui/module-info';
import { usePersistedState } from '../hooks/usePersistedState';
import { getBackendUrl } from '../utils/config';
import DocumentosRelatorioAcessosModal from './documentos/DocumentosRelatorioAcessosModal';

const DocumentosAdmin = ({ user }) => {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('lista'); // lista, criar, editar, estatisticas
  const [documentoSelecionado, setDocumentoSelecionado] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = usePersistedState('docs_categoria', '');
  const [documentoVisualizar, setDocumentoVisualizar] = useState(null);
  const [historicoExpandido, setHistoricoExpandido] = useState(null); // ID do colaborador com histórico expandido
  const [historicoAcessos, setHistoricoAcessos] = useState([]); // Acessos do colaborador selecionado
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [relatorioAcessos, setRelatorioAcessos] = useState(null); // Relatório de acessos detalhado
  const [showRelatorioModal, setShowRelatorioModal] = useState(false);
  
  // Obter user do localStorage se não foi passado via props
  const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'admin';
  const isResponsavel = currentUser.role === 'responsavel';

  // URL centralizado
  const API_URL = getBackendUrl();
  
  // Função para verificar se pode editar/apagar um documento
  const podeEditarDocumento = (doc) => {
    if (isAdmin) return true;
    if (isResponsavel && doc.criado_por_id === currentUser.id) return true;
    // Permitir editar documentos antigos que não têm criado_por_id (antes da implementação)
    if (isResponsavel && !doc.criado_por_id) return true;
    return false;
  };
  
  const CATEGORIAS = [
    'Comunicados Internos',
    'Procedimentos',
    'Normas',
    'Atas',
    'Outros'
  ];

  const [formData, setFormData] = useState({
    titulo: '',
    categoria: 'Comunicados Internos',
    descricao: '',
    conteudo_texto: '',
    exige_confirmacao: true,
    autor: ''
  });

  const [ficheiro, setFicheiro] = useState(null);

  // Carregar documentos
  const carregarDocumentos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = filtroCategoria ? `?categoria=${encodeURIComponent(filtroCategoria)}` : '';
      
      const res = await fetch(`${API_URL}/api/admin/documentos${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDocumentos(data);
      }
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const loadDocs = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = filtroCategoria ? `?categoria=${encodeURIComponent(filtroCategoria)}` : '';
        
        // Adicionar timeout para evitar espera infinita
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos
        
        const res = await fetch(`${API_URL}/api/admin/documentos${params}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (res.ok) {
          const data = await res.json();
          setDocumentos(data);
        } else {
          console.error('Erro ao carregar documentos:', res.status);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.error('Timeout ao carregar documentos');
        } else {
          console.error('Erro ao carregar documentos:', error);
        }
      }
      setLoading(false);
    };
    
    loadDocs();
  }, [filtroCategoria, API_URL]);

  // Carregar histórico de acessos de um colaborador
  const carregarHistoricoAcessos = async (documentoId, colaboradorId) => {
    if (historicoExpandido === colaboradorId) {
      // Fechar se já está aberto
      setHistoricoExpandido(null);
      setHistoricoAcessos([]);
      return;
    }
    
    setLoadingHistorico(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/documentos/${documentoId}/acessos/${colaboradorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setHistoricoAcessos(data.acessos || []);
        setHistoricoExpandido(colaboradorId);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
    setLoadingHistorico(false);
  };

  // Criar documento
  const criarDocumento = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/admin/documentos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const novoDoc = await res.json();
        
        // Upload de ficheiro se houver
        if (ficheiro) {
          try {
            await uploadFicheiro(novoDoc.id);
          } catch (uploadError) {
            console.error('❌ Erro no upload:', uploadError);
            alert(`⚠️ Documento criado mas erro ao carregar ficheiro: ${uploadError.message}`);
            resetForm();
            setView('lista');
            carregarDocumentos();
            return;
          }
        }
        
        alert('✅ Documento criado com sucesso!');
        resetForm();
        setView('lista');
        carregarDocumentos();
      } else {
        let errorMessage = `Erro ${res.status}`;
        try {
          const error = await res.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          try {
            errorMessage = await res.text();
          } catch (e2) {
            // Manter mensagem padrão
          }
        }
        console.error('❌ Erro ao criar documento:', errorMessage);
        alert(`❌ Erro ao criar documento: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Erro ao criar documento:', error);
      alert(`❌ Erro ao criar documento: ${error.message || 'Erro de conexão. Verifique a sua ligação à internet.'}`);
    }
  };

  // Atualizar documento
  const atualizarDocumento = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/documentos/${documentoSelecionado.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        // Upload de ficheiro se houver
        if (ficheiro) {
          try {
            await uploadFicheiro(documentoSelecionado.id);
          } catch (uploadError) {
            alert(`⚠️ Documento atualizado mas erro ao carregar ficheiro: ${uploadError.message}`);
            resetForm();
            setView('lista');
            carregarDocumentos();
            return;
          }
        }
        
        alert('✅ Documento atualizado com sucesso!');
        resetForm();
        setView('lista');
        carregarDocumentos();
      } else {
        const error = await res.json();
        alert(`❌ ${error.detail}`);
      }
    } catch (error) {
      console.error('Erro ao atualizar documento:', error);
      alert('❌ Erro ao atualizar documento');
    }
  };

  // Upload de ficheiro
  const uploadFicheiro = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      const formDataFile = new FormData();
      formDataFile.append('file', ficheiro);
      
      // Verificar tamanho do ficheiro (limite de 50MB)
      const MAX_SIZE = 50 * 1024 * 1024; // 50MB
      if (ficheiro.size > MAX_SIZE) {
        throw new Error(`Ficheiro muito grande. Tamanho máximo: 50MB. Tamanho atual: ${(ficheiro.size / 1024 / 1024).toFixed(2)}MB`);
      }
      
      // Criar AbortController para timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minutos de timeout
      
      const res = await fetch(`${API_URL}/api/admin/documentos/${docId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataFile,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Clonar a resposta ANTES de tentar ler o body
      const resClone = res.clone();
      
      if (!res.ok) {
        let errorMessage = `Erro ${res.status}`;
        try {
          const errorData = await resClone.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Se não conseguir ler JSON, usar texto
          try {
            errorMessage = await res.text();
          } catch (e2) {
            // Manter mensagem padrão
          }
        }
        console.error('Erro upload:', res.status, errorMessage);
        throw new Error(errorMessage);
      }
      
      return true;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('❌ Upload timeout');
        throw new Error('Upload demorou demasiado tempo. Tente novamente ou use um ficheiro menor.');
      }
      console.error('Erro ao fazer upload:', error);
      throw error;
    }
  };

  // Apagar documento
  const apagarDocumento = async (docId) => {
    if (!window.confirm('⚠️ Tem certeza que deseja apagar este documento?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/documentos/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        alert('✅ Documento apagado com sucesso!');
        carregarDocumentos();
      }
    } catch (error) {
      console.error('Erro ao apagar documento:', error);
      alert('❌ Erro ao apagar documento');
    }
  };

  // Carregar estatísticas
  const carregarEstatisticas = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/documentos/${docId}/estatisticas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEstatisticas(data);
        setView('estatisticas');
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      alert('❌ Erro ao carregar estatísticas');
    }
  };

  // Exportar PDF de confirmações de leitura
  const exportarPDF = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/documentos/${docId}/export/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `confirmacoes_${docId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        alert('❌ Erro ao gerar PDF');
      }
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      alert('❌ Erro ao exportar PDF');
    }
  };

  // Carregar relatório de acessos detalhado (como nos horários)
  const carregarRelatorioAcessos = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/documentos/${docId}/relatorio-acessos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setRelatorioAcessos(data);
        setShowRelatorioModal(true);
      } else {
        alert('❌ Erro ao carregar relatório de acessos');
      }
    } catch (error) {
      console.error('Erro ao carregar relatório de acessos:', error);
      alert('❌ Erro ao carregar relatório de acessos');
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      categoria: 'Comunicados Internos',
      descricao: '',
      conteudo_texto: '',
      exige_confirmacao: true,
      autor: ''
    });
    setFicheiro(null);
    setDocumentoSelecionado(null);
  };

  const editarDocumento = (doc) => {
    setDocumentoSelecionado(doc);
    setFormData({
      titulo: doc.titulo,
      categoria: doc.categoria,
      descricao: doc.descricao,
      conteudo_texto: doc.conteudo_texto || '',
      exige_confirmacao: doc.exige_confirmacao,
      autor: doc.autor
    });
    setView('editar');
  };

  const formatarData = (isoString) => {
    if (!isoString) return 'N/A';
    const data = new Date(isoString);
    return data.toLocaleDateString('pt-PT') + ' ' + data.toLocaleTimeString('pt-PT');
  };

  // VIEW: Formulário (Criar/Editar)
  if (view === 'criar' || view === 'editar') {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">
            {view === 'criar' ? 'Novo Documento' : 'Editar Documento'}
          </h2>
          <Button onClick={() => { resetForm(); setView('lista'); }} variant="outline">
            Cancelar
          </Button>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Título *</label>
                <input
                  type="text"
                  value={formData.titulo}
                  onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Procedimento de Segurança"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Categoria *</label>
                <select
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {CATEGORIAS.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descrição Curta *</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Breve descrição do documento"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Conteúdo (Texto)</label>
                <textarea
                  value={formData.conteudo_texto}
                  onChange={(e) => setFormData({...formData, conteudo_texto: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="6"
                  placeholder="Escreva o conteúdo completo do documento aqui..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Ficheiro Anexo (PDF, Word, Excel, etc.)</label>
                
                {/* Ficheiro atual (modo editar) */}
                {view === 'editar' && documentoSelecionado?.ficheiro_nome && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                          Ficheiro atual: {documentoSelecionado.ficheiro_nome}
                        </span>
                      </div>
                      <span className="text-xs text-blue-600">✓ Guardado</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      Carregue um novo ficheiro para substituir
                    </p>
                  </div>
                )}
                
                <input
                  type="file"
                  onChange={(e) => setFicheiro(e.target.files[0])}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                />
                {ficheiro && (
                  <div className="text-sm text-green-600 mt-2">
                    ✓ Novo ficheiro selecionado: {ficheiro.name}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Autor *</label>
                <input
                  type="text"
                  value={formData.autor}
                  onChange={(e) => setFormData({...formData, autor: e.target.value})}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nome do autor"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.exige_confirmacao}
                  onChange={(e) => setFormData({...formData, exige_confirmacao: e.target.checked})}
                  className="w-4 h-4"
                />
                <label className="text-sm font-medium">
                  Exige confirmação de leitura obrigatória
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={view === 'criar' ? criarDocumento : atualizarDocumento}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  size="lg"
                  disabled={!formData.titulo || !formData.categoria}
                >
                  {view === 'criar' ? 'Criar Documento' : 'Guardar Alterações'}
                </Button>
                <Button
                  onClick={() => { resetForm(); setView('lista'); }}
                  variant="outline"
                  size="lg"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // VIEW: Estatísticas
  if (view === 'estatisticas' && estatisticas) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">Estatisticas</h2>
          <Button onClick={() => { setEstatisticas(null); setView('lista'); }} variant="outline">
            Voltar
          </Button>
        </div>

        {/* Documento */}
        <Card className="mb-6">
          <CardHeader className="bg-blue-50">
            <CardTitle>{estatisticas.documento.titulo}</CardTitle>
            <div className="flex gap-2 mt-2">
              <Badge>{estatisticas.documento.categoria}</Badge>
              <span className="text-sm text-gray-600">
                Por {estatisticas.documento.autor}
              </span>
            </div>
          </CardHeader>
        </Card>

        {/* Estatísticas gerais */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold">{estatisticas.total_colaboradores}</div>
                <div className="text-sm text-gray-600">Total Colaboradores</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                <div className="text-2xl font-bold text-green-600">{estatisticas.total_confirmados}</div>
                <div className="text-sm text-gray-600">Confirmados</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <XCircle className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                <div className="text-2xl font-bold text-orange-600">{estatisticas.total_pendentes}</div>
                <div className="text-sm text-gray-600">Pendentes</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <BarChart3 className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                <div className="text-2xl font-bold">{estatisticas.taxa_confirmacao.toFixed(1)}%</div>
                <div className="text-sm text-gray-600">Taxa Confirmação</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Informações adicionais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-5 h-5 text-gray-600" />
                <span className="font-semibold">Total de Acessos</span>
              </div>
              <div className="text-3xl font-bold text-blue-600">{estatisticas.total_acessos}</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-gray-600" />
                <span className="font-semibold">Tempo Médio até Leitura</span>
              </div>
              <div className="text-3xl font-bold text-purple-600">
                {estatisticas.tempo_medio_horas.toFixed(1)}h
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Ações - apenas para admin */}
        {isAdmin && (
          <div className="flex gap-3 mb-6">
            <Button
              onClick={() => exportarPDF(estatisticas.documento.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              <Printer className="w-4 h-4 mr-2" />
              Relatório PDF
            </Button>
            <Button
              onClick={() => carregarRelatorioAcessos(estatisticas.documento.id)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Eye className="w-4 h-4 mr-2" />
              Ver Acessos Detalhados
            </Button>
          </div>
        )}

        {/* Lista de confirmações */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Confirmações de Leitura
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {estatisticas.confirmados.map((conf, idx) => (
                <div key={idx} className="border rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-3 bg-green-50 cursor-pointer hover:bg-green-100 transition-colors"
                    onClick={() => carregarHistoricoAcessos(documentoSelecionado.id, conf.colaborador_id)}
                  >
                    <div>
                      <div className="font-semibold flex items-center gap-2">
                        {conf.colaborador_nome}
                        {historicoExpandido === conf.colaborador_id ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        Confirmado em: {formatarData(conf.data_confirmacao)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-blue-600">
                        {conf.total_acessos} acesso(s)
                      </div>
                      <div className="text-sm text-gray-600">
                        {conf.tempo_visualizacao}s de leitura
                      </div>
                    </div>
                  </div>
                  
                  {/* Histórico expandido */}
                  {historicoExpandido === conf.colaborador_id && (
                    <div className="bg-gray-50 p-3 border-t">
                      <div className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Histórico de Acessos
                      </div>
                      {loadingHistorico ? (
                        <div className="text-center py-2 text-gray-500">A carregar...</div>
                      ) : historicoAcessos.length > 0 ? (
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {historicoAcessos.map((acesso, aIdx) => (
                            <div key={aIdx} className="flex items-center justify-between text-sm p-2 bg-white rounded border">
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3 text-gray-400" />
                                <span>
                                  {new Date(acesso.data_hora).toLocaleDateString('pt-PT', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  })}
                                </span>
                                <span className="text-gray-500">às</span>
                                <span className="font-medium">
                                  {new Date(acesso.data_hora).toLocaleTimeString('pt-PT', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              <span className="text-xs text-gray-400">{acesso.ip}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-2 text-gray-500 text-sm">
                          Sem registos de acesso
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {estatisticas.confirmados.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  Nenhuma confirmação ainda
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Modal de Relatório de Acessos - dentro da view de estatísticas */}
        <DocumentosRelatorioAcessosModal
          show={showRelatorioModal}
          relatorioAcessos={relatorioAcessos}
          onClose={() => {
            setShowRelatorioModal(false);
            setRelatorioAcessos(null);
          }}
        />
      </div>
    );
  }

  // VIEW: Lista
  return (
    <div className="p-6">
      {/* ModuleInfo - Ajuda contextual */}
      <div className="flex items-center gap-2 mb-4">
        <ModuleInfo
          descricao="Gestao de documentos internos da empresa. Permite criar, editar e partilhar documentos por categoria (Comunicados, Procedimentos, Normas, Atas). Pode exigir confirmacao de leitura aos colaboradores e acompanhar quem leu cada documento."
          dados="Documentos guardados em /api/admin/documentos com upload de ficheiros. Estatisticas de acessos e confirmacoes por colaborador. Exportacao PDF do conteudo."
          calculo="Sem calculos. Contagem de documentos por categoria e acessos por colaborador. Limite de upload: 50 MB por ficheiro."
          legendas="Confirmacao exigida = colaboradores devem marcar leitura. Estatisticas = quem leu, quando e quantas vezes. Relatorio = visao consolidada de acessos."
        />
      </div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Gestao de Documentos</h2>
          <p className="text-gray-600">Administração completa do sistema</p>
        </div>
        <Button onClick={() => setView('criar')} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Novo Documento
        </Button>
      </div>

      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
            className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todas as Categorias</option>
            {CATEGORIAS.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">A carregar...</div>
        </div>
      )}

      {/* Lista */}
      {!loading && (
        <div className="space-y-4">
          {documentos.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Nenhum documento criado</p>
                <Button onClick={() => setView('criar')} className="mt-4">
                  Criar Primeiro Documento
                </Button>
              </CardContent>
            </Card>
          ) : (
            documentos.map(doc => (
              <Card key={doc.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{doc.titulo}</CardTitle>
                      <div className="flex gap-2 mt-2">
                        <Badge>{doc.categoria}</Badge>
                        {doc.exige_confirmacao && (
                          <Badge variant="outline">Exige Confirmação</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4">{doc.descricao}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => setDocumentoVisualizar(doc)}
                      variant="default"
                      size="sm"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Documento
                    </Button>
                    <Button
                      onClick={() => carregarEstatisticas(doc.id)}
                      variant="outline"
                      size="sm"
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Estatísticas
                    </Button>
                    {/* Botões de editar/apagar só aparecem se tiver permissão */}
                    {podeEditarDocumento(doc) && (
                      <>
                        <Button
                          onClick={() => editarDocumento(doc)}
                          variant="outline"
                          size="sm"
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Editar
                        </Button>
                        <Button
                          onClick={() => apagarDocumento(doc.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Apagar
                        </Button>
                      </>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 mt-4">
                    Autor: {doc.autor} • Publicado: {formatarData(doc.data_publicacao)}
                    {doc.ficheiro_nome && !doc.ficheiro_existe && (
                      <span className="ml-2 text-red-600 font-medium">
                        ⚠️ Ficheiro em falta - precisa de ser recarregado
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Modal de Visualização de Documento */}
      {documentoVisualizar && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b sticky top-0 bg-white z-10">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-2xl">{documentoVisualizar.titulo}</CardTitle>
                  <div className="flex gap-2 mt-2">
                    <Badge>{documentoVisualizar.categoria}</Badge>
                    <span className="text-sm text-gray-600">
                      {formatarData(documentoVisualizar.data_publicacao)}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => setDocumentoVisualizar(null)}
                  variant="ghost"
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Descrição e Conteúdo */}
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-2">Descrição</h3>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {documentoVisualizar.descricao}
                </p>
              </div>

              {documentoVisualizar.conteudo_texto && (
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-2">Conteúdo</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {documentoVisualizar.conteudo_texto}
                  </p>
                </div>
              )}

              {/* Visualizador PDF inline */}
              {documentoVisualizar.ficheiro_nome && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3 pb-2 border-b">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">{documentoVisualizar.ficheiro_nome}</span>
                      {documentoVisualizar.ficheiro_existe === false && (
                        <span className="text-red-600 text-sm">⚠️ Ficheiro em falta</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Visualizador PDF inline ou mensagem de erro */}
                  {documentoVisualizar.ficheiro_existe === false ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
                      <FileText className="w-16 h-16 text-red-300 mx-auto mb-4" />
                      <h3 className="text-red-800 font-semibold text-lg mb-2">
                        Ficheiro não encontrado
                      </h3>
                      <p className="text-red-600 text-sm mb-4">
                        O ficheiro "{documentoVisualizar.ficheiro_nome}" não está disponível no servidor.
                      </p>
                      <p className="text-gray-600 text-sm">
                        Para resolver, edite o documento e carregue novamente o ficheiro PDF.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Botão para abrir em nova aba - mais fiável */}
                      <div className="flex justify-center gap-4 bg-blue-50 p-4 rounded-lg">
                        <a
                          href={`${API_URL}/api/documentos/${documentoVisualizar.id}/download?token=${localStorage.getItem('token')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Eye className="w-5 h-5" />
                          Abrir em Nova Aba
                        </a>
                        <a
                          href={`${API_URL}/api/documentos/${documentoVisualizar.id}/download?token=${localStorage.getItem('token')}&download=true`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Download className="w-5 h-5" />
                          Descarregar
                        </a>
                      </div>
                      {/* Preview com object - melhor compatibilidade que iframe */}
                      <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '500px' }}>
                        <object
                          data={`${API_URL}/api/documentos/${documentoVisualizar.id}/download?token=${localStorage.getItem('token')}`}
                          type="application/pdf"
                          className="w-full h-full"
                        >
                          <div className="flex flex-col items-center justify-center h-full text-gray-500">
                            <FileText className="w-16 h-16 mb-4" />
                            <p className="text-lg mb-2">Não foi possível carregar o preview</p>
                            <p className="text-sm">Use os botões acima para abrir ou descarregar o ficheiro</p>
                          </div>
                        </object>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Informações adicionais */}
              <div className="text-sm text-gray-600 pt-4 border-t">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <strong>Autor:</strong> {documentoVisualizar.autor}
                  </div>
                  <div>
                    <strong>Publicado:</strong> {formatarData(documentoVisualizar.data_publicacao)}
                  </div>
                  <div>
                    <strong>Categoria:</strong> {documentoVisualizar.categoria}
                  </div>
                  <div>
                    <strong>Exige Confirmação:</strong> {documentoVisualizar.exige_confirmacao ? 'Sim' : 'Não'}
                  </div>
                </div>
              </div>

              {/* Botão Fechar */}
              <div className="mt-6 flex justify-end">
                <Button onClick={() => setDocumentoVisualizar(null)} size="lg">
                  Fechar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Relatório de Acessos */}
      <DocumentosRelatorioAcessosModal
        show={showRelatorioModal}
        relatorioAcessos={relatorioAcessos}
        onClose={() => {
          setShowRelatorioModal(false);
          setRelatorioAcessos(null);
        }}
      />
    </div>
  );
};

export default DocumentosAdmin;
